import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import type { getTrelloWorkspacesQuery } from '#generated/graphql/getTrelloWorkspacesQuery.graphql';
import type { getTrelloWorkspacesPageQuery } from '#generated/graphql/getTrelloWorkspacesPageQuery.graphql';
import {
  TRELLO_WORKSPACE_PAGE_SIZE,
  type TrelloWorkspace,
} from './trelloTypes';

// Two documents rather than one with a nullable $after. Relay always sends
// every declared variable, substituting null when a value is omitted, and
// Trello server validates `after` with a Zod `.optional()` string schema that
// rejects null. A single query would therefore fail validation on the first
// page, resolve `workspaces` to null, and present as "no workspaces".
// The selection sets must stay identical.
const firstPageQuery = graphql`
  query getTrelloWorkspacesQuery($first: Int!) {
    trello {
      me @optIn(to: "TrelloMe") {
        workspaces(first: $first) {
          edges {
            node {
              id
              displayName
              name
              logoUrl
              enterprise {
                atlassianOrganization {
                  id
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

const nextPageQuery = graphql`
  query getTrelloWorkspacesPageQuery($first: Int!, $after: String!) {
    trello {
      me @optIn(to: "TrelloMe") {
        workspaces(first: $first, after: $after) {
          edges {
            node {
              id
              displayName
              name
              logoUrl
              enterprise {
                atlassianOrganization {
                  id
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

type WorkspaceConnection = NonNullable<
  NonNullable<
    NonNullable<getTrelloWorkspacesQuery['response']['trello']>['me']
  >['workspaces']
>;

type WorkspaceEdges = NonNullable<WorkspaceConnection['edges']>;

// Trello stores logos as `<hash>/<size>.png`. The `logoUrl` field returns only
// the prefix, which is not an object, so fetching it returns 403 rather than
// 404 — the buckets deny anonymous ListBucket, which masks the missing key.
// Trello web appends the size at the call site for the same reason, see
// TemplatesTile.js and PluginModelSerializer.js.
const LOGO_SIZE_SEGMENT = '170.png';

const withLogoSize = (logoUrl: string | null | undefined): string | null => {
  if (!logoUrl) return null;
  return logoUrl.endsWith('.png') ? logoUrl : `${logoUrl}/${LOGO_SIZE_SEGMENT}`;
};

function toWorkspaces(edges: WorkspaceEdges): TrelloWorkspace[] {
  return edges
    .map((edge) => edge?.node)
    .filter(
      (node): node is NonNullable<typeof node> =>
        node != null && node.id != null,
    )
    .map((node) => ({
      id: node.id,
      displayName: node.displayName ?? node.id,
      atlassianOrganizationId:
        node.enterprise?.atlassianOrganization?.id ?? null,
      shortName: node.name ?? null,
      logoUrl: withLogoSize(node.logoUrl),
    }));
}

// Both checks are strict on purpose. `hasNextPage` must be exactly true and
// `endCursor` a non-empty string, otherwise a placeholder value stands in for
// a real cursor and the same page is requested again.
function nextCursor(connection: WorkspaceConnection | null | undefined) {
  const pageInfo = connection?.pageInfo;
  if (pageInfo?.hasNextPage !== true) {
    return null;
  }
  const cursor = pageInfo.endCursor;
  return typeof cursor === 'string' && cursor.length > 0 ? cursor : null;
}

/**
 * Fetches the current user's Trello workspaces via AGG.
 *
 * The first page is loaded through Relay suspense so the selector renders as
 * soon as there is something to show. If the member has more workspaces than
 * one page, the rest are fetched in the background with cursor pagination and
 * appended as each page arrives, so the dropdown fills in progressively rather
 * than being silently truncated.
 *
 * Cursor pagination is only honoured by Trello server when the
 * `trello_member_workspaces_pagination` flag is enabled. While that flag is
 * off the resolver ignores `after` and reports `hasNextPage` by comparing the
 * returned count against the requested count, so the first page simply
 * arrives with `hasNextPage: false` and no follow-up request is made.
 *
 * The slug and logo are always requested rather than gated behind the
 * multi-workspace selector flag. Tying them to a feature gate made the fields
 * depend on when the gate was read relative to the query, which silently
 * dropped avatars and workspace URLs.
 */
export type TrelloWorkspacesResult = {
  workspaces: TrelloWorkspace[];
  /** True while a `loadMore` request is in flight. */
  isLoadingMore: boolean;
  /** True when the server reported another page after the ones already held. */
  hasMore: boolean;
  /** Fetches the next page. No-op while loading or when exhausted. */
  loadMore: () => void;
};

export function useTrelloWorkspaces(): TrelloWorkspacesResult {
  const environment = useRelayEnvironment();
  const firstPage = useLazyLoadQuery<getTrelloWorkspacesQuery>(firstPageQuery, {
    first: TRELLO_WORKSPACE_PAGE_SIZE,
  });

  const firstConnection = firstPage.trello?.me?.workspaces;

  // Snapshot of the first page, taken once. `useLazyLoadQuery` re-reads the
  // Relay store on every render, and later pages write records that the
  // first-page selector no longer resolves, which made page one disappear from
  // the list as soon as a later page arrived. Holding the edges in state keeps
  // the accumulated list independent of subsequent store writes.
  const [firstEdges, setFirstEdges] = useState<WorkspaceEdges>(
    () => firstConnection?.edges ?? [],
  );
  const seededFirstPage = useRef(false);

  useEffect(() => {
    if (seededFirstPage.current) {
      return;
    }
    const edges = firstConnection?.edges;
    if (edges != null) {
      seededFirstPage.current = true;
      setFirstEdges(edges);
    }
  }, [firstConnection]);

  const [extraEdges, setExtraEdges] = useState<WorkspaceEdges>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(() =>
    nextCursor(firstConnection),
  );

  const requestedCursors = useRef(new Set<string>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadMore = useCallback(() => {
    if (
      cursor == null ||
      isLoadingMore ||
      requestedCursors.current.has(cursor)
    ) {
      return;
    }

    requestedCursors.current.add(cursor);
    setIsLoadingMore(true);

    void (async () => {
      try {
        const data = await fetchQuery<getTrelloWorkspacesPageQuery>(
          environment,
          nextPageQuery,
          { first: TRELLO_WORKSPACE_PAGE_SIZE, after: cursor },
        ).toPromise();

        if (!mounted.current) {
          return;
        }

        const connection = data?.trello?.me?.workspaces;
        if (!connection) {
          setCursor(null);
          return;
        }

        const edges = connection.edges ?? [];
        if (edges.length > 0) {
          setExtraEdges((previous) => [...previous, ...edges]);
        }
        setCursor(nextCursor(connection));
      } catch {
        if (mounted.current) {
          setCursor(null);
        }
      } finally {
        if (mounted.current) {
          setIsLoadingMore(false);
        }
      }
    })();
  }, [cursor, environment, isLoadingMore]);

  const workspaces = useMemo(
    () => toWorkspaces([...firstEdges, ...extraEdges]),
    [firstEdges, extraEdges],
  );

  return { workspaces, isLoadingMore, hasMore: cursor != null, loadMore };
}
